import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class BrowserService {
    private isBrowser: boolean;

    constructor(@Inject(PLATFORM_ID) platformId: Object) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    get isPlatformBrowser(): boolean {
        return this.isBrowser;
    }

    get window(): Window | null {
        return this.isBrowser ? window : null;
    }

    get document(): Document | null {
        return this.isBrowser ? document : null;
    }


    getElementById<T extends HTMLElement = HTMLElement>(id: string): T | null {
        return this.isBrowser && this.document
            ? (this.document.getElementById(id) as T | null)
            : null;
    }

    querySelector<T extends Element = Element>(selector: string): T | null {
        return this.isBrowser && this.document
            ? (this.document.querySelector(selector) as T | null)
            : null;
    }

    clear(): void {
        if (this.localStorage) {
            this.localStorage.clear();
        }
    }

    get localStorage(): Storage | null {
        return this.isBrowser ? localStorage : null;
    }

    get sessionStorage(): Storage | null {
        return this.isBrowser ? sessionStorage : null;
    }

    open(url: string, target: string = '_blank') {
        if (this.isBrowser) {
            window.open(url, target);
        }
    }

    get locationHref(): any | null {
        return this.isBrowser ? window.location.href : null;
    }

    set locationHref(url: any) {
        if (this.isBrowser) {
            window.location.href = url;
        }
    }

    get locationOrigin(): any | null {
        return this.isBrowser ? window.location.origin : null;
    }

    get hostname(): any | null {
        return this.isBrowser ? window.location.hostname : null;
    }

    get scrollTop(): any | null {
        return this.isBrowser && this.document
            ? this.document.documentElement.scrollTop
            : null;
    }

    set scrollTop(value: number) {
        if (this.isBrowser && this.document) {
            this.document.documentElement.scrollTop = value;
        }
    }

    createElement(tagName: string): any | null {
        if (this.isBrowser) {
            return document.createElement(tagName);
        }
        return null;
    }

    onVisibilityChange(handler: () => void): void {
        if (this.isBrowser && typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handler);
        }
    }

    removeVisibilityChange(handler: () => void): void {
        if (this.isBrowser && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', handler);
        }
    }

    get isDocumentHidden(): boolean {
        return this.isBrowser && typeof document !== 'undefined'
            ? document.hidden
            : false; // default to visible in SSR
    }

    querySelectorAll(selector: string): any | null {
        return this.isBrowser && typeof document !== 'undefined'
            ? document.querySelectorAll(selector)
            : null;
    }

    // Append to document body
    appendToBody(element: HTMLElement): void {
        if (this.isBrowser && typeof document !== 'undefined') {
            document.body.appendChild(element);
        }
    }

    // Remove from document body
    removeFromBody(element: HTMLElement): void {
        if (this.isBrowser && typeof document !== 'undefined' && document.body.contains(element)) {
            document.body.removeChild(element);
        }
    }

    // Revoke object URL
    revokeObjectURL(url: any): any {
        if (this.isBrowser && typeof window !== 'undefined' && typeof URL !== 'undefined') {
            URL.revokeObjectURL(url);
        }
    }

    // Create Object URL
    createObjectURL(blob: Blob): string | null {
        if (this.isBrowser && typeof window !== 'undefined' && typeof URL !== 'undefined') {
            return URL.createObjectURL(blob);
        }
        return null;
    }

    getElementByClassName<T extends HTMLElement = HTMLElement>(className: any, index = 0): T | null {
        if (this.isPlatformBrowser && typeof document !== 'undefined') {
            const elements = document.getElementsByClassName(className);
            return elements.length > index ? elements[index] as T : null;
        }
        return null;
    }

}
